import { useState, useEffect, useCallback, useRef } from 'react';
import { showToast } from './toast';

// Block D20 — uniform notification deep-link contract.
//
// THE CONTRACT (one sentence): a notification deep-link is a section path plus a single
// `?<entity>=<id>` query param; on open the app routes to that section and publishes the
// (entity, id) via `setDeepLink`, which the section consumes — once, via `consumeDeepLink`
// on mount or the `aidey:open-item` event while mounted — to focus that entity, then clears it.
//
// A future section follows the same three steps: add a `linkFor` builder (backend), read its
// `?<entity>=` param here, and consume `(entity, id)` to open the item. No new pattern.

// contact_filter (FU-1, decision 43): /contacts?contact_filter=follow-up opens Contacts already
// filtered to "Needs follow up". Same contract: a section path plus one ?<entity>=<id>.
// family_tab (UI-4, decision 56): Personal's sidebar "Calendar" opens the Family page on its calendar tab.
// proposals_contact / bookings_contact (decision 82): a contact's record opens Proposals or All Bookings
// already filtered to that contact (the id is the contact's).
// note (Carla's item 6): the Dashboard's Notes popup "Open in Notes" opens the Notes page on that note.
// note_new (decision 90 v2): "+ New note" on a contact, a project or a task opens Notes' chooser with
// the link pre-filled; the id is "<contact|project|task>:<the record's id>".
export const DEEP_LINK_ENTITIES = ['booking', 'invoice', 'task', 'contract', 'contact', 'portal_messages', 'portal', 'form', 'proposal', 'contact_filter', 'family_tab', 'invoice_new', 'proposals_contact', 'bookings_contact', 'note', 'note_new'];

let pending = null; // { entity, id }, set on navigation, consumed by the target section.

// Seed from the current URL at module load (a cold open from a push: the SW opened
// /scheduler?booking=X&nc=...). Doing this at import time — before any component effect —
// means the target section's consumeDeepLink on mount finds it, avoiding an effect-order race.
try {
  const _p = new URL(window.location.href);
  // Block 203a: iterate DEEP_LINK_ENTITIES rather than a second hardcoded copy of it —
  // the two had to be edited together, and adding an entity to only one silently
  // half-wired it (cold opens would miss, warm ones would work).
  for (const e of DEEP_LINK_ENTITIES) {
    const id = _p.searchParams.get(e);
    if (id) { pending = { entity: e, id: String(id) }; break; }
  }
} catch {}

// Called by App.js when a notification link is opened (push or bell). Stores the target so a
// not-yet-mounted section can pick it up on mount, and fires an event for an already-mounted one.
export function setDeepLink(entity, id) {
  if (!entity || !id) return;
  pending = { entity, id: String(id) };
  try { window.dispatchEvent(new CustomEvent('aidey:open-item', { detail: { entity, id: String(id) } })); } catch {}
}

// A section calls this on mount: returns the id if a deep-link is pending for its entity, and
// clears it so it fires only once.
export function consumeDeepLink(entity) {
  if (pending && pending.entity === entity) { const id = pending.id; pending = null; return id; }
  return null;
}

// Uniform per-section consumer. Returns [targetId, clear] — the id to open (from a pending
// deep-link on mount OR the aidey:open-item event while mounted), and a clearer to call once
// the section has opened it. The section waits for its data before opening:
//   const [openId, clearOpen] = useDeepLinkTarget('invoice');
//   useEffect(() => { if (openId && invoices.length) { openInvoice(openId); clearOpen(); } }, [openId, invoices]);
export function useDeepLinkTarget(entity) {
  const [id, setId] = useState(() => consumeDeepLink(entity));
  // Item 8 (2026-10-12): a section that goes away before it has answered the link (App remounting
  // while the session settles was the case) hands it back, so the next one to mount still opens it.
  const unanswered = useRef(id);
  unanswered.current = id;
  useEffect(() => {
    const p = consumeDeepLink(entity); // catch anything set between render and this effect
    if (p) setId(p);
    const handler = (e) => { if (e.detail && e.detail.entity === entity) setId(e.detail.id); };
    window.addEventListener('aidey:open-item', handler);
    return () => {
      window.removeEventListener('aidey:open-item', handler);
      if (unanswered.current && !pending) pending = { entity, id: String(unanswered.current) };
    };
  }, [entity]);
  const clear = useCallback(() => { unanswered.current = null; setId(null); }, []);
  return [id, clear];
}

// Block D21/D23 — cross-remount handoff. The SW writes { url, notifContext, ts } to IndexedDB
// in notificationclick (both warm and cold paths) because (i) iOS drops the openWindow URL on a
// cold launch, and (ii) after any deploy the SW force-reloads open tabs, remounting the app mid-
// delivery. The record must survive that remount, so:
//   - readPendingDeepLink() PEEKS — it does NOT delete. App.js reads it on every boot; a remount
//     a moment later re-reads the same record and re-opens the item. Returned only if recent
//     (within FRESH_MS). This is the D23 change from the old delete-on-read.
//   - clearPendingDeepLink() deletes the record — the section calls it only AFTER it has actually
//     opened the item (never on read), and it also strips the ?<entity>= query from the URL at
//     the same moment (fix (a)), so neither channel re-triggers once the item is open.
// Leak safety: the store holds a SINGLE key ('latest'), so there is at most one record ever, the
// next tap overwrites it, and the FRESH_MS gate means a never-cleared record is ignored (never
// acted on) after the window — it cannot resurface days later.
export const DEEP_LINK_FRESH_MS = 120000; // ~120s: tap-to-open should be near-immediate

function withDeepLinkStore(mode, work) {
  return new Promise(resolve => {
    let done = false;
    const finish = v => { if (!done) { done = true; resolve(v); } };
    try {
      if (typeof indexedDB === 'undefined') return finish(null);
      const req = indexedDB.open('aidey-deeplink', 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('pending')) db.createObjectStore('pending');
      };
      req.onsuccess = () => {
        try {
          const db = req.result;
          const tx = db.transaction('pending', mode);
          const store = tx.objectStore('pending');
          const out = work(store);
          tx.oncomplete = () => { db.close(); finish(out.value); };
          tx.onerror = () => { db.close(); finish(null); };
          tx.onabort = () => { db.close(); finish(null); };
        } catch { finish(null); }
      };
      req.onerror = () => finish(null);
      req.onblocked = () => finish(null);
    } catch { finish(null); }
  });
}

// PEEK — read without deleting. Returns the record only if it's fresh, else null.
export function readPendingDeepLink() {
  const out = { value: null };
  return withDeepLinkStore('readonly', (store) => {
    const getReq = store.get('latest');
    getReq.onsuccess = () => {
      const rec = getReq.result || null;
      out.value = (rec && rec.url && rec.ts && (Date.now() - rec.ts) <= DEEP_LINK_FRESH_MS) ? rec : null;
    };
    return out;
  });
}

// Called by the section once it has actually opened the item. It strips the ?<entity>= query
// from the URL (retires the redundant URL channel) but DELIBERATELY does NOT delete the
// IndexedDB record.
//
// Why not delete: on device the item OPENS first (~5ms, in-process) and the SW's post-deploy
// force-reload REMOUNTS a beat later (~100ms+, network-bound). Deleting the record on open would
// remove it before that remount can use it — exactly the "opened, then reset by the remount"
// failure. Instead the record is left to expire via the FRESH_MS gate in readPendingDeepLink, so
// a remount inside the window re-reads it and re-opens the item (idempotently), and after the
// window it is inert. Single-key store + next-tap-overwrites + freshness gate ⇒ no leak.
export function clearPendingDeepLink() {
  try {
    const u = new URL(window.location.href);
    let changed = false;
    for (const e of DEEP_LINK_ENTITIES) if (u.searchParams.has(e)) { u.searchParams.delete(e); changed = true; }
    if (u.searchParams.has('nc')) { u.searchParams.delete('nc'); changed = true; }
    if (changed) window.history.replaceState({}, '', u.pathname + (u.search || ''));
  } catch {}
}

// Pull the (entity, id) out of a deep-link URL's query, e.g. "/scheduler?booking=abc".
export function entityFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    for (const e of DEEP_LINK_ENTITIES) {
      const id = parsed.searchParams.get(e);
      if (id) return { entity: e, id };
    }
  } catch {}
  return null;
}

// Carla's item 8 (2026-10-12): a notification opens its item, and when the item no longer exists
// the person is told so, never left on a page that silently shows something else.
export const ITEM_GONE = 'This item is no longer available';
export function sayItemGone() { showToast(`${ITEM_GONE}.`, { type: 'info', ms: 6000 }); }

// One consumer for a section whose items are a loaded list:
//   useDeepLinkItem('invoice', { ready: loaded, find: (id) => invoices.find(...), open: (inv) => ... })
// ready: the list has loaded (NOT "has items": an empty list is an answer, and the item is gone).
// A failed load is not ready: the section shows its own failure and the link waits.
export function useDeepLinkItem(entity, { ready, find, open }) {
  const [id, clear] = useDeepLinkTarget(entity);
  const latest = useRef({ find, open });
  latest.current = { find, open };
  useEffect(() => {
    if (!id || !ready) return;
    const item = latest.current.find(String(id));
    if (item) latest.current.open(item, String(id)); else sayItemGone();
    clear();
    clearPendingDeepLink();   // D23: retire the deep link once it has been answered
  }, [id, ready, clear]);
  return id;
}
